import { useContext, useMemo } from 'react';

import { pathToRegexp } from 'path-to-regexp';
import { useResource } from 'react-resource-router';

import {
  addMenuItemsById,
  createNestedMenu,
  findNestedMenuItem,
  MenuItem,
  ROOT_MENU_ID,
} from '@atlassian/bitbucket-navigation';

import { CurrentDataContext } from 'src/app/current-data';
import { NavDataContext } from 'src/app/nav-data';
import { ComponentLinkProxy } from 'src/components/component-link';
import { pathnameMatchesForgeMenuItemUrl } from 'src/components/forge-ui/src/utils/utils';
import { ContainerNavigationProps } from 'src/components/navigation/src/components/container-navigation-v3';
import { FORGE_MENU_ITEM_TYPE } from 'src/components/navigation/src/utils/create-nested-menu';
import { useHistory__UNSAFE, useLocation, useMatch } from 'src/router/hooks';
import { accountNavigationResource } from 'src/sections/account/account-navigation-resource';
import { publishUiEvent } from 'src/utils/analytics/publish';
import { REPO_FULL_SLUG_REGEX } from 'src/utils/validate-repository-path';

const useMenuItemsWithForge = () => {
  const {
    menuItems: resourceMenuItems,
    forgeMenuItems,
    forgeAdminMenuItems,
  } = useContext(NavDataContext);
  const { repository } = useContext(CurrentDataContext);
  const { path: matchPath } = useMatch();

  // memoizing menuItems to avoid unnecessary re-rendering on workspace settings pages
  // (e.g. rerender on groups/:groupSlug page causes the tab to reset to users when you
  // try to select projects & repositories)
  const menuItems = useMemo(() => {
    const sidebarMenuItems = resourceMenuItems?.sidebar || [];
    if (!sidebarMenuItems.length) {
      return sidebarMenuItems;
    }
    let items = createNestedMenu(sidebarMenuItems);

    if (repository) {
      if (forgeMenuItems?.length) {
        items = addMenuItemsById(items, ROOT_MENU_ID, forgeMenuItems);
      }

      if (forgeAdminMenuItems?.length) {
        items = addMenuItemsById(
          items,
          'repo-settings-link',
          forgeAdminMenuItems
        );
      }
    } else if (matchPath.startsWith('/:workspaceSlug/workspace/settings')) {
      // This check is here to ensure cached workspace forge menu item resources are not appended to other menus.
      // Outside of the repo section, forge menu items should only be available on workspace settings pages.
      if (forgeAdminMenuItems?.length) {
        items = addMenuItemsById(items, ROOT_MENU_ID, forgeAdminMenuItems);
      }
    }

    return items;
  }, [
    forgeAdminMenuItems,
    forgeMenuItems,
    matchPath,
    repository,
    resourceMenuItems?.sidebar,
  ]);

  return menuItems;
};

export const useMenuItems = () => {
  const { workspace, project, repository } = useContext(CurrentDataContext);
  const { data: accountData } = useResource(accountNavigationResource);
  let { pathname } = useLocation();
  let { url: matchUrl } = useMatch();

  const menuItems = useMenuItemsWithForge();

  // TODO: Update error handling
  if (!repository && !workspace && !project && !accountData) {
    return { menuItems: [], selectedMenuItem: undefined };
  }

  if (workspace && pathname.includes(workspace.uuid)) {
    // check if url contains uuid and replace with slug to match menuItems
    pathname = pathname.replace(workspace.uuid, workspace.slug);
    matchUrl = matchUrl.replace(workspace.uuid, workspace.slug);
  }
  const path = pathname.replace(/\/$/, '');

  // Get the selected menu item
  let selectedMenuItem = undefined;
  if (repository) {
    const repoSettingsPathRegex = new RegExp(/^\/[^/]+\/[^/]+\/admin($|\/)/);
    selectedMenuItem = findNestedMenuItem(menuItems, (item: MenuItem) => {
      const prefixes = item.matching_url_prefixes || [];
      const itemUrl = item.url.replace(/\/$/, '');

      // compare the paths after the repoSubpath because repo URLs can be UUIDs or slugs
      const repoPathBase = pathToRegexp(`/${REPO_FULL_SLUG_REGEX}`, [], {
        end: false,
      });
      const normalizedPathname = pathname.replace(repoPathBase, '');
      const normalizedItemUrl = itemUrl.replace(repoPathBase, '');
      const pathNameWithoutTrailingSlash = pathname.replace(/\/$/, '');

      if (item.type === FORGE_MENU_ITEM_TYPE) {
        return pathnameMatchesForgeMenuItemUrl(
          pathNameWithoutTrailingSlash,
          itemUrl
        );
      } else if (repoSettingsPathRegex.test(pathname)) {
        // This conditional checks if the current path is under the admin (settings) section of the repo path structure.
        // The pathname and item url comparison is exact because some of the settings menu item urls are subpaths of the others.
        // It also checks the menu item "matching_url_prefixes" for any menu items which need to be
        // highlighted in the UI for more than one route. e.g. /admin/webhooks and /admin/webhooks/new
        return (
          pathNameWithoutTrailingSlash === itemUrl ||
          (pathNameWithoutTrailingSlash.startsWith(itemUrl) &&
            prefixes.some(prefix => normalizedPathname.startsWith(prefix)))
        );
      } else {
        return (
          normalizedPathname.indexOf(normalizedItemUrl) === 0 ||
          prefixes.some(prefix => normalizedPathname.startsWith(prefix))
        );
      }
    });
  } else {
    selectedMenuItem = findNestedMenuItem(menuItems, (item: MenuItem) => {
      const subpath = pathname.slice(matchUrl.length);
      const prefixes = item.matching_url_prefixes || [];
      const itemUrl = item.url.replace(/\/$/, '');

      return (
        path === itemUrl ||
        prefixes.some(
          prefix =>
            subpath.startsWith(prefix) || // match if subpath starts with prefix
            (path.startsWith(itemUrl) && prefix === '*') || // match if path startsWith itemUrl and prefix is *
            path === prefix // accept override if entire path matches prefix (e.g /some-workspace-name)
        )
      );
    });
  }

  return { menuItems, selectedMenuItem };
};

export const useContainerNavigationProps = (): ContainerNavigationProps => {
  const history = useHistory__UNSAFE();
  const { menuItems, selectedMenuItem } = useMenuItems();

  return {
    menuItems,
    selectedMenuItem,
    linkComponent: ComponentLinkProxy,
    history,
    publishUiEvent,
  };
};
